import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Animated, View as RNView, TouchableWithoutFeedback, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { View } from '@shared/components/View';
import { ColorThemeContext, grid } from '@shared/styles';
import { useKeyboardHeight } from '@shared/util/hooks';
import { MBBottomSheetBackdrop } from './backdrop';
import { useSwipeGesture } from './gestures';
import { useDimensions, useTitleBar } from './hooks';
import { Props } from './props';
import { useFade, useSlide } from './tweens';

/**
 * This compensates for the spring animation overshoot distance and prevents showing the background when the view
 * is animated past its resting place due to overshoot. The value is determined by observation.
 */
const SPRING_OVERSHOOT_MARGIN = 20;

export function MBBottomSheetMobile({
  children,
  onClose,
  title,
  renderTitleBar,
  initialSize = 'full',
}: Props) {
  const { initialTop, fullTop } = useDimensions();
  const keyboardHeight = useKeyboardHeight();
  const { opacity, fadeIn, fadeOut } = useFade({ duration: 300, value: 0.5 });
  const { translateY, slideUp, slideDown } = useSlide({ duration: 200, value: initialTop });
  const openAnim = useRef(Animated.parallel([fadeIn, slideUp])).current;
  const close = useCallback(
    () => Animated.parallel([fadeOut, slideDown]).start(onClose),
    [onClose, fadeOut, slideDown],
  );
  const { top } = useSwipeGesture(initialTop, fullTop, close, () => openAnim.stop(), initialSize);

  const safeAreaInsets = useSafeAreaInsets();
  const safeAreaInsetBottomPadding = !!keyboardHeight ? 0 : safeAreaInsets.bottom;

  useEffect(() => openAnim.start(), [openAnim]);

  const colorTheme = useContext(ColorThemeContext);
  const contentStyle = useMemo<Animated.WithAnimatedValue<ViewStyle>>(
    () => ({
      backgroundColor: colorTheme.background,
      position: 'absolute',
      top,
      bottom: -SPRING_OVERSHOOT_MARGIN + keyboardHeight,
      paddingBottom: SPRING_OVERSHOOT_MARGIN + safeAreaInsetBottomPadding,
      borderTopLeftRadius: grid(2),
      borderTopRightRadius: grid(2),
      overflow: 'hidden',
      width: '100%',
      transform: [{ translateY }],
      cursor: 'default',
    }),
    [translateY, top, keyboardHeight, colorTheme, safeAreaInsetBottomPadding],
  );

  const backdropStyle = useMemo<ViewStyle>(
    () => ({
      width: '100%',
      backgroundColor: colorTheme.background,
      bottom: 0,
      height: keyboardHeight,
      position: 'absolute',
    }),
    [keyboardHeight, colorTheme],
  );

  const titleBar = useTitleBar(title, renderTitleBar);

  return (
    <MBBottomSheetBackdrop onClose={close} opacity={opacity}>
      {!!keyboardHeight && <RNView style={backdropStyle} />}
      <TouchableWithoutFeedback onPress={event => event.stopPropagation()}>
        <Animated.View style={contentStyle}>
          <View fill>
            <View>{titleBar}</View>
            {children}
          </View>
        </Animated.View>
      </TouchableWithoutFeedback>
    </MBBottomSheetBackdrop>
  );
}
